/**
 * Describe your model
 * @class
 */
import {dispatcher} from '@netx/core-next/instance/dispatcher';
import Base from '@netx/core/lib/netx-model';

class Pager extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/**
	 * @property {object} defaults                 - Model defaults
	 * @property {string} defaults.currentPage     - Current page number
	 * @property {number} defaults.itemTotal       - Total number of items
	 * @property {number} defaults.pageSize        - Items per page
	 * @property {number} defaults.pagesToDisplay  - Number of numeric page options to display
	 * @property {number} defaults.pageTotal       - Total number of pages
	 * @property {boolean} defaults.routesToPage   - Changing page causes route (TODO: this should not be handled in the pager model itself)
	 */
	get defaults() {
		return {
			currentPage: 1,
			itemTotal: 0,
			pageSize: 50,
			pagesToDisplay: 8,
			pageTotal: 0,
			startIndex: null,
			routesToPage: true,
		};
	}

	/**
	 * When passing options to a madel, Backbone will look for a definition of the model's collection and if we should parse the initial attributes (which also receives our options object)
	 * All options are then passed along to the set method which is fired off the bat to set the model's attributes
	 * @override
	 * @param {object} attrs Model attributes
	 * @param {object} opts Model options
	 */
	constructor(attrs, opts = {}) {
		super(attrs, opts);

		// Debounced/throttled methods
		this.changedCurrentPage = _.debounce(this.changedCurrentPage, 50);

		// Hey, listen!
		this.on('change:itemTotal change:pageSize', this.calculatePages).on(
			'change:currentPage',
			this.changedCurrentPage,
		);

		if (this.collection) {
			// To avoid any pager total collision, when we fetch we do not want to listen
			// for add and removes until after the sync
			this.listenTo(this.collection, 'request:read', () => {
				this.stopListening(this.collection, 'add').stopListening(
					this.collection,
					'remove',
				);
			});

			this.listenTo(this.collection, 'destroy', () => {
				const assetCount = this.get('itemTotal');
				assetCount && this.set('itemTotal', assetCount - 1);
			}).listenTo(this.collection, 'sync', (collection, results, opts) => {
				if (!collection.selection) return;

				const p = {};
				p.firstIndex = collection.selection.startPosition;
				p.lastIndex = p.firstIndex + collection.length - 1; // i think this is smarter.
				p.firstPage = p.firstIndex == 1;
				p.lastChunk = this.lastChunk;

				// logic: imagine chunkSize = 50, then ...
				// startPosition 1 = page 1
				// startPosition 2 - 51 = page 2 ( < 51 is an abnormal start position, but it's ahead of some assets)
				// startPosition 52 = page 3, and so on.
				p.currentPage = Math.ceil((p.firstIndex - 1) / collection.pageSize) + 1;

				if (this.has('itemTotal')) {
					// 1 = 1 page.  51- 100 = 2 pages.  101 = 3 pages. And so on.
					p.pageTotal = Math.ceil(this.get('itemTotal') / collection.pageSize);
				}

				this.set(p);

				// To be safe, if anything rogue fires syncs without a `request:read`
				// cleanup
				this.stopListening(this.collection, 'add')
					.stopListening(this.collection, 'remove')
					.listenTo(this.collection, 'add', (model, collection, opts) => {
						if (opts.index == null || opts.success != null) return;
						this.adjustTotal(1);
					})
					.listenTo(this.collection, 'remove', (model, collection, opts) => {
						if (opts.index == null || opts.success != null) return;
						this.adjustTotal(-1);
					});
			});
		}
	}

	/**
	 * Calculate the total asset count after add or remove
	 * @param {number} adj (+1 or -1)
	 * @method
	 */
	adjustTotal(adj) {
		const assetCount = this.get('itemTotal');
		assetCount && this.set('itemTotal', assetCount + adj);
	}

	/**
	 * Calculate the total number of pages required given itemTotal and pageSize
	 * @method
	 */
	calculatePages() {
		const pageTotal = Math.ceil(this.get('itemTotal') / this.get('pageSize'));
		this.set('pageTotal', pageTotal);
	}

	/**
	 * @method
	 * @param {module:app/models/pager} pager Pager (this)
	 * @param {number} page Page
	 * @param {object} opts Options
	 */
	changedCurrentPage(pager, page, opts) {
		const collection = this.collection;

		if (!collection) return;

		// see if this pager uses the route
		if (this.get('routesToPage')) {
			dispatcher.trigger('router:page', page, this.collection.routeContext);
			return;
		}

		!opts.ignoreGoToPage && this.collection.gotoPage(page, opts);
	}

	/**
	 * @method
	 * @param {object} opts Options
	 */
	firstPage(opts) {
		this.set('currentPage', 1, opts);
	}

	/**
	 * @method
	 * @return {object} PageDTO
	 */
	getPageDTO() {
		const pageDTO = {
			size: this.get('pageSize'),
		};

		if (this.get('startId') != null) {
			pageDTO.startId = this.get('startId');
		} else {
			// This is 0 based pager
			pageDTO.startIndex = (this.get('currentPage') - 1) * this.get('pageSize');
		}

		return pageDTO;
	}

	/**
	 * @method
	 * @param {number} page Page number
	 * @param {object} opts Options
	 */
	gotoPage(page, opts) {
		this.set('currentPage', parseInt(page), opts);
	}

	/**
	 * @method
	 * @param {object} opts Options
	 */
	lastPage(opts) {
		this.set('currentPage', this.get('pageTotal'), opts);
	}

	/**
	 * @method
	 * @param {object} opts Options
	 */
	nextPage(opts) {
		const currentPage = this.get('currentPage');
		if (currentPage >= this.get('pageTotal')) return;

		this.set('currentPage', currentPage + 1, opts);
	}

	/**
	 * @method
	 * @param {object} opts Options
	 */
	prevPage(opts) {
		const currentPage = this.get('currentPage');
		if (currentPage <= 1) return;

		this.set('currentPage', currentPage - 1, opts);
	}

	/**
	 * We get back a start index on paging calls
	 * @method
	 * @param {number} index Index
	 */
	setStartIndex(index, opts) {
		return this.set(
			{
				currentPage: Math.floor(index / this.get('pageSize') + 1),
				startIndex: index,
			},
			{fetch: false, ...opts},
		);
	}
	/**
	 * When clearing the current cart we need to reset the pager
	 * without trashing instance specific attributes
	 * @method
	 */
	clearPages() {
		this.set({
			currentPage: 1,
			firstIndex: 1,
			itemTotal: 0,
			pageTotal: 0,
		});
	}

	/** @type {string} */
	static get __name__() {
		return 'Pager';
	}
}

export default Pager;
